import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Save, Plus, Trash2, ChevronDown, ChevronUp,
  FileText, Star, Upload, MessageSquare, MapPin,
  GripVertical, AlertTriangle, Settings
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const FormEditor = ({ discordId }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedType, setExpandedType] = useState(null);
  const [expandedSection, setExpandedSection] = useState("types");

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/form-config`);
      setConfig(res.data);
    } catch (err) {
      toast.error("Failed to load form config");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/form-config?discord_id=${discordId}`, config);
      toast.success("Form configuration saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const updateType = (index, field, value) => {
    const types = [...config.feedback_types];
    types[index] = { ...types[index], [field]: value };
    setConfig({ ...config, feedback_types: types });
  };

  const addType = () => {
    const newType = {
      id: `custom-${Date.now()}`,
      name: "New Feedback Type",
      hide_severity: false,
      hide_incident: false,
      description_label: "What happened?",
      description_placeholder: "Please describe in detail...",
      questions: []
    };
    setConfig({ ...config, feedback_types: [...config.feedback_types, newType] });
    setExpandedType(config.feedback_types.length);
  };

  const removeType = (index) => {
    if (!window.confirm(`Remove "${config.feedback_types[index].name}"?`)) return;
    const types = config.feedback_types.filter((_, i) => i !== index);
    setConfig({ ...config, feedback_types: types });
    setExpandedType(null);
  };

  const addQuestion = (typeIndex) => {
    const types = [...config.feedback_types];
    types[typeIndex].questions = [
      ...types[typeIndex].questions,
      { label: "New Question", placeholder: "Enter your answer..." }
    ];
    setConfig({ ...config, feedback_types: types });
  };

  const removeQuestion = (typeIndex, qIndex) => {
    const types = [...config.feedback_types];
    types[typeIndex].questions = types[typeIndex].questions.filter((_, i) => i !== qIndex);
    setConfig({ ...config, feedback_types: types });
  };

  const updateQuestion = (typeIndex, qIndex, field, value) => {
    const types = [...config.feedback_types];
    types[typeIndex].questions[qIndex] = { ...types[typeIndex].questions[qIndex], [field]: value };
    setConfig({ ...config, feedback_types: types });
  };

  const addLocation = () => {
    setConfig({ ...config, incident_locations: [...(config.incident_locations || []), "New Location"] });
  };

  const removeLocation = (index) => {
    const locs = config.incident_locations.filter((_, i) => i !== index);
    setConfig({ ...config, incident_locations: locs });
  };

  const updateLocation = (index, value) => {
    const locs = [...config.incident_locations];
    locs[index] = value;
    setConfig({ ...config, incident_locations: locs });
  };

  const updateStarLabel = (index, value) => {
    const labels = [...config.star_rating_labels];
    labels[index] = value;
    setConfig({ ...config, star_rating_labels: labels });
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (!config) return <p className="text-zinc-500 text-center py-8">Failed to load configuration</p>;

  const SectionHeader = ({ icon: Icon, title, section, color = "text-blue-400" }) => (
    <button
      onClick={() => setExpandedSection(expandedSection === section ? null : section)}
      className="w-full flex items-center justify-between p-3 rounded-none bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
      data-testid={`section-toggle-${section}`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="font-medium text-white text-sm">{title}</span>
      </div>
      {expandedSection === section ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <Settings className="w-5 h-5 text-purple-400" />
          <div>
            <h3 className="text-lg font-heading font-bold text-white">Form Editor</h3>
            <p className="text-zinc-500 text-sm">Configure the public feedback form</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 bg-emerald-500/20 border border-emerald-500/30 hover:bg-emerald-500/30 text-emerald-400 rounded-none transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50"
          data-testid="save-form-config-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {/* General Settings */}
      <SectionHeader icon={FileText} title="General Settings" section="general" color="text-cyan-400" />
      {expandedSection === "general" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 p-4 rounded-none border border-zinc-700 bg-zinc-800/20">
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Form Title</label>
            <input type="text" value={config.form_title || ""} onChange={(e) => setConfig({ ...config, form_title: e.target.value })} className="input-field w-full" data-testid="config-form-title" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Form Description</label>
            <textarea value={config.form_description || ""} onChange={(e) => setConfig({ ...config, form_description: e.target.value })} className="input-field w-full h-16 resize-none" data-testid="config-form-description" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Subtitle</label>
            <input type="text" value={config.form_subtitle || ""} onChange={(e) => setConfig({ ...config, form_subtitle: e.target.value })} className="input-field w-full" />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Warning Text</label>
            <input type="text" value={config.warning_text || ""} onChange={(e) => setConfig({ ...config, warning_text: e.target.value })} className="input-field w-full" />
          </div>
        </motion.div>
      )}

      {/* Feedback Types */}
      <SectionHeader icon={FileText} title={`Feedback Types (${config.feedback_types?.length || 0})`} section="types" color="text-purple-400" />
      {expandedSection === "types" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
          {config.feedback_types?.map((type, ti) => (
            <div key={type.id || ti} className="rounded-none border border-zinc-700 bg-zinc-800/20 overflow-hidden">
              <button
                onClick={() => setExpandedType(expandedType === ti ? null : ti)}
                className="w-full flex items-center justify-between p-3 hover:bg-zinc-800/50 transition-colors"
                data-testid={`type-toggle-${ti}`}
              >
                <div className="flex items-center gap-2">
                  <GripVertical className="w-4 h-4 text-zinc-600" />
                  <span className="text-white text-sm font-medium">{type.name}</span>
                  <span className="text-zinc-600 text-xs">({type.questions?.length || 0} questions)</span>
                  {type.hide_severity && <span className="px-1.5 py-0.5 text-[10px] bg-blue-500/20 text-blue-400 rounded">no severity</span>}
                  {type.hide_incident && <span className="px-1.5 py-0.5 text-[10px] bg-blue-500/20 text-blue-400 rounded">no incident</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); removeType(ti); }}
                    className="p-1 hover:bg-red-500/20 rounded transition-colors"
                    data-testid={`remove-type-${ti}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                  {expandedType === ti ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                </div>
              </button>

              {expandedType === ti && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 border-t border-zinc-700 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Type Name</label>
                      <input type="text" value={type.name} onChange={(e) => updateType(ti, "name", e.target.value)} className="input-field w-full" data-testid={`type-name-${ti}`} />
                    </div>
                    <div className="flex items-end gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={type.hide_severity} onChange={(e) => updateType(ti, "hide_severity", e.target.checked)} className="rounded border-zinc-600 bg-zinc-800 text-blue-500" />
                        <span className="text-xs text-zinc-400">Hide Severity</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={type.hide_incident} onChange={(e) => updateType(ti, "hide_incident", e.target.checked)} className="rounded border-zinc-600 bg-zinc-800 text-blue-500" />
                        <span className="text-xs text-zinc-400">Hide Incident Fields</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Description Label</label>
                    <input type="text" value={type.description_label || ""} onChange={(e) => updateType(ti, "description_label", e.target.value)} className="input-field w-full" />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-500 mb-1">Description Placeholder</label>
                    <input type="text" value={type.description_placeholder || ""} onChange={(e) => updateType(ti, "description_placeholder", e.target.value)} className="input-field w-full" />
                  </div>

                  {/* Questions */}
                  <div className="border-t border-zinc-700 pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-zinc-500 uppercase">Custom Questions</span>
                      <button
                        onClick={() => addQuestion(ti)}
                        className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                        data-testid={`add-question-${ti}`}
                      >
                        <Plus className="w-3 h-3" /> Add Question
                      </button>
                    </div>
                    {type.questions?.length === 0 && (
                      <p className="text-zinc-600 text-xs italic">No custom questions for this type</p>
                    )}
                    {type.questions?.map((q, qi) => (
                      <div key={qi} className="flex gap-2 mb-2 items-start">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={q.label}
                            onChange={(e) => updateQuestion(ti, qi, "label", e.target.value)}
                            className="input-field w-full text-sm"
                            placeholder="Question label"
                            data-testid={`question-label-${ti}-${qi}`}
                          />
                          <input
                            type="text"
                            value={q.placeholder}
                            onChange={(e) => updateQuestion(ti, qi, "placeholder", e.target.value)}
                            className="input-field w-full text-sm"
                            placeholder="Placeholder text"
                          />
                        </div>
                        <button
                          onClick={() => removeQuestion(ti, qi)}
                          className="p-1.5 hover:bg-red-500/20 rounded transition-colors mt-1"
                          data-testid={`remove-question-${ti}-${qi}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          ))}

          <button
            onClick={addType}
            className="w-full py-2.5 border border-dashed border-zinc-600 hover:border-slate-500 rounded-none text-zinc-500 hover:text-zinc-300 text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="add-feedback-type-btn"
          >
            <Plus className="w-4 h-4" /> Add Feedback Type
          </button>
        </motion.div>
      )}

      {/* Star Rating */}
      <SectionHeader icon={Star} title="Star Rating" section="stars" color="text-yellow-400" />
      {expandedSection === "stars" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-none border border-zinc-700 bg-zinc-800/20 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={config.star_rating_enabled ?? true} onChange={(e) => setConfig({ ...config, star_rating_enabled: e.target.checked })} className="rounded border-zinc-600 bg-zinc-800 text-yellow-500" />
            <span className="text-sm text-zinc-400">Enable Star Rating</span>
          </label>
          {config.star_rating_enabled !== false && (
            <div className="grid grid-cols-5 gap-2">
              {(config.star_rating_labels || []).map((label, i) => (
                <div key={`star-${i + 1}`}>
                  <label className="block text-[10px] text-zinc-600 mb-1">{i + 1} Star</label>
                  <input type="text" value={label} onChange={(e) => updateStarLabel(i, e.target.value)} className="input-field w-full text-xs" data-testid={`star-label-${i}`} />
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Evidence Section */}
      <SectionHeader icon={Upload} title="Evidence Section" section="evidence" color="text-emerald-400" />
      {expandedSection === "evidence" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-none border border-zinc-700 bg-zinc-800/20 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={config.evidence_enabled ?? true} onChange={(e) => setConfig({ ...config, evidence_enabled: e.target.checked })} className="rounded border-zinc-600 bg-zinc-800 text-emerald-500" />
            <span className="text-sm text-zinc-400">Enable Evidence Section</span>
          </label>
          {config.evidence_enabled !== false && (
            <>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Section Label</label>
                <input type="text" value={config.evidence_label || ""} onChange={(e) => setConfig({ ...config, evidence_label: e.target.value })} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Description</label>
                <input type="text" value={config.evidence_description || ""} onChange={(e) => setConfig({ ...config, evidence_description: e.target.value })} className="input-field w-full" />
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* Suggestions Section */}
      <SectionHeader icon={MessageSquare} title="Suggestions Section" section="suggestions" color="text-purple-400" />
      {expandedSection === "suggestions" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-none border border-zinc-700 bg-zinc-800/20 space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={config.suggestions_enabled ?? true} onChange={(e) => setConfig({ ...config, suggestions_enabled: e.target.checked })} className="rounded border-zinc-600 bg-zinc-800 text-purple-500" />
            <span className="text-sm text-zinc-400">Enable Suggestions Section</span>
          </label>
          {config.suggestions_enabled !== false && (
            <>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Section Label</label>
                <input type="text" value={config.suggestions_label || ""} onChange={(e) => setConfig({ ...config, suggestions_label: e.target.value })} className="input-field w-full" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Placeholder</label>
                <input type="text" value={config.suggestions_placeholder || ""} onChange={(e) => setConfig({ ...config, suggestions_placeholder: e.target.value })} className="input-field w-full" />
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* Incident Locations */}
      <SectionHeader icon={MapPin} title="Incident Locations" section="locations" color="text-orange-400" />
      {expandedSection === "locations" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-none border border-zinc-700 bg-zinc-800/20 space-y-2">
          {(config.incident_locations || []).map((loc, i) => (
            <div key={`loc-${i}-${loc.slice(0, 10)}`} className="flex gap-2">
              <input type="text" value={loc} onChange={(e) => updateLocation(i, e.target.value)} className="input-field flex-1 text-sm" data-testid={`location-${i}`} />
              <button onClick={() => removeLocation(i)} className="p-1.5 hover:bg-red-500/20 rounded transition-colors">
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </button>
            </div>
          ))}
          <button onClick={addLocation} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-testid="add-location-btn">
            <Plus className="w-3 h-3" /> Add Location
          </button>
        </motion.div>
      )}

      {/* Bottom Save */}
      <div className="pt-4 border-t border-zinc-700">
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 bg-emerald-500/20 border border-emerald-500/30 hover:bg-emerald-500/30 text-emerald-400 rounded-none transition-colors flex items-center justify-center gap-2 font-medium disabled:opacity-50"
          data-testid="save-form-config-bottom-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save All Changes"}
        </button>
      </div>
    </div>
  );
};

export default FormEditor;
